import type { SessionManager } from './managers/SessionManager';
import type { LogData } from '@easterngraphics/wcf/modules/eaiws/session';
import type { FetchOptions } from '@easterngraphics/wcf/modules/utils/async';
import type { DialogManager } from '@egr/xbox/egr-gui-dialogs/DialogManager';
import type { SceneManager } from '@egr/xbox/threeD/managers/SceneManager';
import type { AjaxSoapErrorInfo, FetchSoapErrorInfo } from '@egr/xbox/utils/AjaxErrorHandler';
import type { ErrorOptions } from '@egr/xbox/utils/errors/Error';
import type { PromiseCallbacks } from '@egr/xbox/utils/promise/Callbacks';

import i18next from 'i18next';
import * as React from 'react';
import 'pepjs'; // pointerEvent polyfill
import '@easterngraphics/wcf/styles/cf.css';
import '@easterngraphics/wcf/styles/core.css';
import '@easterngraphics/wcf/styles/pl.css';

import { appEvents } from './AppEvents';
import { AppInitialization } from './AppInitialization';
import { EaiwsBaskets } from './EaiwsBaskets';
import { SessionExpiredError } from './custom-errors/SessionExpiredError';
import { ExportManager } from './managers/ExportManager';

import { OBK_IMAGE_SIZE } from '@egr/xbox/basket-view/Utils/Constants';
import { languageFromI18next } from '@egr/xbox/egr-gui-elements/LanguageProvider';
import { registerAjaxErrorHandler, retryNetworkRequests } from '@egr/xbox/utils/AjaxErrorHandler';
import { assert, logPropertyAccess, wrapDebugTimer } from '@egr/xbox/utils/Debug';
import { getEnvBoolean, getEnvArray, getEnvVar } from '@egr/xbox/utils/ReactScriptHelper';
import { deviceHasMouse, ios } from '@egr/xbox/utils/UserAgent';
import { clearDataErrorBreadcrumbs } from '@egr/xbox/utils/errors/DataErrors';
import { captureException, errorTrackingEnabled } from '@egr/xbox/utils/errors/Error';
import { getPromiseCallbacks } from '@egr/xbox/utils/promise/Callbacks';

import { Application } from '@easterngraphics/wcf/modules/core';
import { EaiwsSession, SoapError } from '@easterngraphics/wcf/modules/eaiws';
import { wcfConfig } from '@easterngraphics/wcf/modules/utils';
import { AjaxAbortedError } from '@easterngraphics/wcf/modules/utils/async';

export interface AppProps {}

const getAppPromiseCallbacks: PromiseCallbacks<App> = getPromiseCallbacks<App>();

export function getApp<T extends App>(): Promise<T> {
    return getAppPromiseCallbacks.promise as unknown as Promise<T>;
}

export type App = BaseApp<AppProps>;

export interface DefaultBaseAppProps {
    app: App;
}

export abstract class BaseApp<TProps extends AppProps = AppProps> extends React.Component<TProps, {}> {
    public readonly version: string = getEnvVar('BUILD_COMMIT', 'develop');
    // Important: must be initialized by the child class
    public abstract readonly sessionManager: SessionManager;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public abstract readonly sceneManager: SceneManager<any>;
    public abstract readonly dialogManager: DialogManager<BaseApp<TProps>>;

    // will be initialized in this class
    public readonly coreApp: Application;
    public eaiwsBaskets: EaiwsBaskets;
    public abstract readonly exportManager: ExportManager;

    // should be resolved after the app was fully initialized
    public readonly initialization: AppInitialization;

    constructor(props: TProps) {
        super(props);

        this.initialization = new AppInitialization();
        assert(i18next.isInitialized, 'i18next instance must be initialized');

        // register error handlers
        registerAjaxErrorHandler({
            networkError: this.handleNetworkError.bind(this, retryNetworkRequests),
            soapError: (error: AjaxSoapErrorInfo | FetchSoapErrorInfo): void => {
                error.reject();
            },
            eaiwsSessionExpired: (error: AjaxSoapErrorInfo | FetchSoapErrorInfo): void => {
                if ((error as AjaxSoapErrorInfo).responseError != null) {
                    error.reject(new SessionExpiredError((error as AjaxSoapErrorInfo).responseError));
                } else if ((error as FetchSoapErrorInfo).error != null) {
                    error.reject(new SessionExpiredError((error as FetchSoapErrorInfo).error));
                }
            },
        });

        const ErrorIgnoreList = getEnvArray('DEBUG_IGNORE_ERRORS', '|');

        window.addEventListener('unhandledrejection', async (event: PromiseRejectionEvent): Promise<void> => {
            const reason: Error =
                event.reason instanceof Error ? event.reason : event.reason != null ? new Error(event.reason) : new Error('unhandledrejection without .reason');

            if (ErrorIgnoreList.includes(reason.message)) {
                console.error('Ignored error', reason);
                return;
            }

            if (errorTrackingEnabled() && !(reason instanceof AjaxAbortedError)) {
                let extraOptions: ErrorOptions | undefined;

                if (reason instanceof SoapError) {
                    try {
                        const log: LogData | undefined = await this.eaiwsBaskets.session.session.getSessionLog(true);
                        if (log != null) {
                            extraOptions = { extra: { eaiwsLog: log.data } };
                        }
                    } catch {
                        // do nothing
                    }
                }

                captureException(reason, 'fatal', extraOptions);
            }

            window.setTimeout((): void => {
                this.handleUnhandledRejection(reason, event.reason instanceof SessionExpiredError);
            });
        });

        getAppPromiseCallbacks.resolve(this);

        if (getEnvBoolean('DEBUG_APP_TRAP', false)) {
            return logPropertyAccess('App', this);
        }
    }

    public override shouldComponentUpdate(nextProps: AppProps, nextState: {}): boolean {
        // Ensure that this component is never updated in order to keep the
        // canvas 3d context alive.
        // (Which should not happen under normal conditions anyway, since
        // this component has no state and no props.)
        return false;
    }

    public override componentDidMount(): void {
        this.initializeCore();
    }

    public abstract handleNetworkError(retryCallback: VoidFunction): void;
    protected abstract handleUnhandledRejection(error: Error, forceReload?: boolean): void;

    protected initializeCore(): void {
        wcfConfig.articleConfigurationOptions = {
            fetchPropValueImages: true,
            trimPropText: true, // Todo: check if correct value
        };
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        (this.coreApp as Application) = new Application();

        this.initializeCoreBasket();

        // enable w-cf hover effects
        if (document.documentElement != null && (!ios || deviceHasMouse())) {
            document.documentElement.classList.add('wcf-hover-enabled');
        }

        appEvents.session.close.addListener((event): void => {
            event.tasks.cleaning.addTask('clear core document', async (): Promise<void> => {
                if (this.eaiwsBaskets.basket.articleManager != null) {
                    this.coreApp.document.clear();
                }
            });
            event.tasks.cleaning.addTask('clearDataErrorBreadcrumbs', async () => clearDataErrorBreadcrumbs());
        });

        appEvents.session.open.addListener((event): void => {
            event.tasks.preProcessing.addTask('setLocale', async (): Promise<void> => {
                await this.sessionManager.setLocale(languageFromI18next());
                this.eaiwsBaskets.session.basket.onGetRequestOptions = this.onGetRequestOptions;
            });

            event.tasks.postProcessing.addTask('set image options', async (): Promise<void> => {
                void this.eaiwsBaskets.basket.setArticleImageOptions({ resolution: OBK_IMAGE_SIZE });
            });

        });
    }

    protected initializeCoreBasket(): void {
        const session = new EaiwsSession();
        if (getEnvBoolean('DEBUG_TRACE_OBK_UPLOAD', false)) {
            session.uploadFileToUrl = wrapDebugTimer('upload file to url', session.uploadFileToUrl.bind(session));
        }

        this.eaiwsBaskets = new EaiwsBaskets(session);
        this.eaiwsBaskets.basket.includeCalculationErrorsInInconsistencies = false;
    }

    protected onGetRequestOptions(value: string): FetchOptions | undefined {
        switch (value) {
            case 'getExportedGeometry':
                return ExportManager.fetchOptions;
        }
        return;
    }
}

export default App;